// Global Instructions Rule Applied!
//
// LP States strip (LP agent programme Phase 7): the account-level twin of
// ProspectStatesStrip, bound to the LP relationship manager's catalogue —
// relationship strength, stance, engagement cadence, open asks, key-person
// transitions.
//
// This is the row an LP account never had. A portfolio company's Dashboard
// carries a Current State strip; an LP account carried only the open signals.
// Mounting this above them makes an LP Dashboard structurally identical to a
// portfolio one (owner decision 5). Since 2026-09-06 the signals are their own
// section on every surface rather than a footer inside this one.
//
// The derived LP status (Active / Target / Historic / Declined) rides in
// titleSuffix, not in the card row: it is not a state — no evidence, no
// confidence, nothing to override — and its colour comes from cfg
// (comms_lp_statuses.tag_color), so this file keeps no colour map (decision 7).
// It reads "Target LP", not "Target": until 2026-09-06 it sat at the far right
// beside 'Agent-maintained', where a bare status word said nothing about what
// it was a status OF. The noun is appended HERE rather than in
// cfg.comms_lp_statuses, whose labels also head the LP Allocation grid's "LP
// status" column and the "LP statuses" filters — "LP statuses: Target LP"
// would read worse than the problem it fixes.
//
// The server filters by agent_type_key, which is what keeps a DUAL-ROLE
// account (portfolio company + LP) from showing its company states here.
import React, { useCallback } from 'react'
import { Tag } from 'antd'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  createTaskFromLpState,
  fetchLpStates,
  overrideLpState
} from '../services/internalAgentsService.ts'
import { LpState } from '../../../state_manager/data_models/internal_agents_model.ts'
import { StatesStrip } from './StatesStrip.tsx'
import { AgentDraftsToggle } from './AgentDraftsToggle.tsx'
import { StateItem } from './StateCard.tsx'

const LP_AGENT_TYPE_KEY = 'lp_relationship_manager'

interface LpStatesStripProps {
  accountId: string
  title?: string
  /** The derived LP status from the account detail read
   *  (get_account_data_v3 → comms_lp_statuses__label / __tag_color). A null
   *  colour means render no tag — the 'None' status, i.e. an account with an
   *  LP role but neither a commitment nor an open prospect. */
  lpStatusLabel?: string | null
  lpStatusTagColor?: string | null
  /** Per-LP agent drafts override (PR D): accounts.agent_drafts_enabled and
   *  what its Default resolves to. Omit onDraftsChange to hide the toggle. */
  draftsEnabled?: boolean | null
  draftsDefaultLabel?: string | null
  onDraftsChange?: (next: boolean | null) => void
  /** Pre-selects assignees in the create-task form (the account's primary
   *  relationship manager when the host has it). Never auto-assigns. */
  defaultAssignees?: Array<{ label: string; value: string }>
  /** Fired after a task is created so the host can refresh its Tasks panel. */
  onTaskCreated?: () => void
  /** Hands `load` to the account form so its Refresh button reloads this panel. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const toStateItem = (s: LpState): StateItem => ({
  id: s.lp_states__id,
  stateKey: s.lp_state_types__state_key,
  label: s.lp_state_types__label,
  kind: s.lp_state_types__kind,
  conditionKey: s.lp_states__condition_key,
  conditionLabel: s.condition_label,
  conditionRank: s.condition_rank,
  summary: s.lp_states__summary,
  status: s.lp_states__status,
  startedAt: s.lp_states__started_at,
  lastConfirmedAt: s.lp_states__last_confirmed_at,
  lastEvidenceAt: s.lp_states__last_evidence_at ?? null,
  lastCheckedAt: s.lp_states__last_checked_at ?? null,
  expectedEndAt: s.lp_states__expected_end_at ?? null,
  expectedEndBasis: s.lp_states__expected_end_basis ?? null,
  userSet: s.lp_states__user_set,
  conditions: s.conditions ?? [],
  taskId: s.lp_states__task_id ?? null,
  taskSubject: s.tasks__subject ?? null,
  taskCompleted: s.tasks__completed ?? null,
  taskDueBy: s.tasks__due_by ?? null
})

export const LpStatesStrip: React.FC<LpStatesStripProps> = ({
  accountId,
  title = 'Current State',
  lpStatusLabel = null,
  lpStatusTagColor = null,
  draftsEnabled = null,
  draftsDefaultLabel = null,
  onDraftsChange,
  defaultAssignees = [],
  onTaskCreated,
  onRefreshReady = null
}) => {
  const token = () => getAccessToken(store.getState().auth)

  const fetchStates = useCallback(
    async () => (await fetchLpStates(accountId, LP_AGENT_TYPE_KEY, token())).map(toStateItem),
    [accountId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const overrideState = useCallback(
    async (args: {
      stateKey: string
      action: 'set_condition' | 'edit_summary' | 'close'
      stateId?: string | null
      conditionKey?: string | null
      summary?: string | null
    }) =>
      overrideLpState(
        {
          account_id: accountId,
          state_key: args.stateKey,
          action: args.action,
          state_id: args.stateId,
          condition_key: args.conditionKey,
          summary: args.summary
        },
        token()
      ),
    [accountId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const createTask = useCallback(
    async (stateId: string, taskRecord: unknown) => createTaskFromLpState(stateId, taskRecord, token()),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <StatesStrip
      title={title}
      fetchStates={fetchStates}
      overrideState={overrideState}
      createTaskFromState={createTask}
      defaultAssignees={defaultAssignees}
      onTaskCreated={onTaskCreated}
      subjectNoun='LP'
      collapseKey='lp_dashboard'
      defaultCollapsed={false}
      onRefreshReady={onRefreshReady}
      titleSuffix={
        <span className='inline-flex items-center gap-2'>
          {/* The guard also suppresses the 'None' status, whose cfg tag_color
              is blank — so "None LP" can never render. */}
          {lpStatusLabel && lpStatusTagColor ? (
            <Tag color={lpStatusTagColor} className='m-0'>
              {`${lpStatusLabel} LP`}
            </Tag>
          ) : null}
          {/* Per-LP agent drafts (PR D) — the relationship's own switch,
              beside the status it defaults from. */}
          {onDraftsChange ? (
            <span className='inline-flex items-center gap-1 font-normal normal-case tracking-normal'>
              <span className='text-gray-400 text-[11.5px]'>Agent drafts</span>
              <AgentDraftsToggle value={draftsEnabled} defaultLabel={draftsDefaultLabel} onChange={onDraftsChange} />
            </span>
          ) : null}
        </span>
      }
      // The view returns persistent placeholders only where a LIVE relationship
      // manager instance exists, so an empty strip means "not followed", not
      // "nothing known". Say which.
      emptyText='The LP relationship manager is not following this account yet.'
    />
  )
}
